/**
 * Project-local action history — tracks add/remove/update actions.
 * Stored at .skaile/history.yaml within the project directory.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import { parse, stringify } from "yaml";

/**
 * A single record in the project's asset action history.
 *
 * @docLink packages/asset-manager/api-reference#history
 */
export interface HistoryEntry {
  /** Asset ref in `kind:name` form (e.g. `skill:my-skill`). */
  ref: string;
  /** Action performed on the asset. */
  action: "add" | "remove" | "update";
  /** ISO 8601 timestamp of when the action was recorded. */
  timestamp: string;
  /** Whether this action was performed in project scope or global-install scope. */
  context: "project" | "global";
}

function historyFile(projectDir: string): string {
  return join(projectDir, ".skaile", "history.yaml");
}

/**
 * Load all history entries from `.skaile/history.yaml` in the project directory.
 *
 * @param projectDir - Absolute path to the workspace root.
 * @returns Array of history entries, oldest first. Returns `[]` when the file does not exist.
 * @docLink packages/asset-manager/api-reference#history
 */
export function loadHistory(projectDir: string): HistoryEntry[] {
  const file = historyFile(projectDir);
  if (!existsSync(file)) return [];
  try {
    const data = parse(readFileSync(file, "utf8")) as { entries?: unknown[] } | null;
    return (data?.entries ?? []).map((e) => {
      const d = e as Record<string, unknown>;
      return {
        ref: String(d.ref ?? ""),
        action: String(d.action ?? "add") as HistoryEntry["action"],
        timestamp: String(d.timestamp ?? new Date().toISOString()),
        context: String(d.context ?? "project") as HistoryEntry["context"],
      };
    });
  } catch {
    return [];
  }
}

/**
 * Append a single action to the history log, capping the total at 500 entries.
 *
 * @param projectDir - Absolute path to the workspace root.
 * @param entry - The history entry to record.
 * @docLink packages/asset-manager/api-reference#history
 */
export function appendHistory(projectDir: string, entry: HistoryEntry): void {
  const entries = loadHistory(projectDir);
  entries.push(entry);
  const trimmed = entries.slice(-500);
  const file = historyFile(projectDir);
  mkdirSync(join(projectDir, ".skaile"), { recursive: true });
  writeFileSync(file, stringify({ entries: trimmed }));
}

/**
 * Return the most recent history entries, newest first.
 *
 * @param projectDir - Absolute path to the workspace root.
 * @param limit - Maximum number of entries to return. Defaults to `20`.
 * @returns Most recent entries in reverse-chronological order.
 * @docLink packages/asset-manager/api-reference#history
 */
export function getRecentHistory(projectDir: string, limit = 20): HistoryEntry[] {
  return loadHistory(projectDir).slice(-limit).reverse();
}

/**
 * Erase all history entries by writing an empty list to `.skaile/history.yaml`.
 *
 * @param projectDir - Absolute path to the workspace root.
 * @docLink packages/asset-manager/api-reference#history
 */
export function clearHistory(projectDir: string): void {
  const file = historyFile(projectDir);
  mkdirSync(join(projectDir, ".skaile"), { recursive: true });
  writeFileSync(file, stringify({ entries: [] }));
}
